const DURATION = 3000;
const FLIGHT_START = 1800;
const ART_WIDTH = 808;
const ART_HEIGHT = 215;
const clamp = (value: number) => Math.max(0, Math.min(1, value));
const ease = (value: number) => { const p = clamp(value); return p * p * (3 - 2 * p); };
type Pose = { x: number; y: number; scale: number };
const between = (from: Pose, to: Pose, progress: number): Pose => ({
    x: from.x + (to.x - from.x) * progress,
    y: from.y + (to.y - from.y) * progress,
    scale: from.scale + (to.scale - from.scale) * progress,
});

/** One visual object: reverse the words, then land on the measured header artwork. */
export async function animateBrandArrival(): Promise<void> {
    const brand = document.querySelector<HTMLElement>('.main-header-layout .menu9-logo-brand');
    // startprawko.pl lands on the home page; gate on the page itself, not on content that moves between pages.
    if (location.hash !== '#startprawko' || !document.querySelector('.ps-home') || !brand) return;
    const clearSignal = () => {
        // A section selected during the effect belongs to the visitor.
        if (location.hash === '#startprawko') history.replaceState(history.state, '', location.pathname + location.search);
    };
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    if (motion.matches) { clearSignal(); return; }
    const scene = document.querySelector<HTMLElement>('[data-brand-arrival]')!;
    const symbol = scene.querySelector<HTMLElement>('.ps-brand-arrival__symbol')!;
    const art = scene.querySelector<HTMLElement>('.ps-brand-arrival__art')!;
    const words = scene.querySelector<HTMLElement>('.ps-brand-arrival__words')!;
    const start = scene.querySelector<HTMLElement>('.ps-brand-arrival__start')!;
    const prawko = scene.querySelector<HTMLElement>('.ps-brand-arrival__prawko')!;
    const halo = scene.querySelector<HTMLElement>('.ps-brand-arrival__halo')!;
    // Clone actual artwork in both themes, without duplicate ids or accessible names.
    const images = Array.from(brand.querySelectorAll<HTMLImageElement>('.menu9-logo-img'));
    const copies = [symbol, art].flatMap((layer) => images.map((image) => {
        const copy = image.cloneNode() as HTMLImageElement;
        copy.removeAttribute('id');
        copy.alt = '';
        copy.loading = 'eager';
        layer.append(copy);
        return copy;
    }));
    await Promise.all([document.fonts.ready, ...copies.map((image) => image.decode().catch(() => undefined))]);
    if (location.hash !== '#startprawko' || motion.matches || document.hidden) {
        copies.forEach((image) => image.remove()); clearSignal(); return;
    }
    const activeImage = () => images.find((image) => getComputedStyle(image).display !== 'none')!;
    if (!activeImage().naturalWidth) { copies.forEach((image) => image.remove()); clearSignal(); return; }
    document.body.append(scene);
    scene.hidden = false;
    const startWidth = start.getBoundingClientRect().width;
    const prawkoWidth = prawko.getBoundingClientRect().width;
    const domainWidth = Math.max(ART_WIDTH, words.offsetLeft + words.scrollWidth + 20);
    scene.style.setProperty('--ps-arrival-width', `${domainWidth}px`);
    const centered = (): Pose => {
        const scale = Math.min(0.9, (window.innerWidth - 36) / domainWidth);
        return { x: (window.innerWidth - domainWidth * scale) / 2, y: (window.innerHeight - ART_HEIGHT * scale) / 2, scale };
    };
    const destination = (): Pose => {
        const box = activeImage().getBoundingClientRect();
        // The header uses object-fit: contain; align the painted artwork, not its larger img box.
        const scale = Math.min(box.width / ART_WIDTH, box.height / ART_HEIGHT);
        return { x: box.left + (box.width - ART_WIDTH * scale) / 2,
            y: box.top + (box.height - ART_HEIGHT * scale) / 2, scale };
    };
    let pose = centered();
    let from = pose;
    let target = destination();
    let flightStart = FLIGHT_START;
    let flying = false;
    let dirty = false;
    let frame = 0;
    let finished = false;
    const markDirty = () => { dirty = true; };
    const resize = new ResizeObserver(markDirty);
    const theme = new MutationObserver(markDirty);
    let complete: () => void;
    const done = new Promise<void>((resolve) => { complete = resolve; });
    const cleanup = () => {
        if (finished) return;
        finished = true;
        cancelAnimationFrame(frame);
        // Atomic handoff: neither doubled logo nor an empty frame.
        scene.hidden = true;
        brand.removeAttribute('data-brand-arriving');
        copies.forEach((image) => image.remove());
        resize.disconnect(); theme.disconnect();
        window.removeEventListener('resize', markDirty);
        window.removeEventListener('scroll', markDirty);
        window.removeEventListener('pagehide', cleanup);
        document.removeEventListener('visibilitychange', onVisibility);
        motion.removeEventListener('change', cleanup);
        clearSignal(); complete();
    };
    const onVisibility = () => { if (document.hidden) cleanup(); };
    resize.observe(brand);
    theme.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    window.addEventListener('resize', markDirty, { passive: true });
    window.addEventListener('scroll', markDirty, { passive: true });
    window.addEventListener('pagehide', cleanup, { once: true });
    document.addEventListener('visibilitychange', onVisibility);
    motion.addEventListener('change', cleanup, { once: true });
    brand.setAttribute('data-brand-arriving', '');
    const began = performance.now();
    let previous = began;
    const render = (now: number) => {
        const elapsed = now - began;
        if (elapsed >= DURATION || !brand.isConnected) { cleanup(); return; }
        // Read target geometry only on changes; all writes affect a fixed visual layer.
        if (elapsed >= FLIGHT_START && !flying) { flying = true; from = pose; target = destination(); }
        if (dirty && flying) {
            const next = destination();
            if (Math.abs(next.x - target.x) + Math.abs(next.y - target.y) + Math.abs(next.scale - target.scale) > 0.01) {
                from = pose; target = next; flightStart = elapsed;
            }
        }
        if (flying) {
            // Paint the exact current destination before the atomic handoff.
            // A late header layout change must not leave a residual jump at the end.
            pose = elapsed >= DURATION - 50
                ? destination()
                : between(from, target, ease((elapsed - flightStart) / (DURATION - flightStart)));
        } else {
            pose = between(pose, centered(), 1 - Math.exp(-(now - previous) / 90));
        }
        dirty = false; previous = now;
        const swap = ease((elapsed - 600) / 900);
        const arc = Math.sin(swap * Math.PI) * 64;
        const morph = ease((elapsed - FLIGHT_START) / 650);
        scene.style.transform = `translate3d(${pose.x}px, ${pose.y}px, 0) scale(${pose.scale})`;
        scene.style.opacity = String(ease(elapsed / 160));
        start.style.transform = `translate3d(${prawkoWidth * swap}px, ${-arc}px, 0)`;
        prawko.style.transform = `translate3d(${-startWidth * swap}px, ${arc}px, 0)`;
        words.style.opacity = String(1 - morph);
        art.style.opacity = String(morph);
        halo.style.opacity = String(1 - ease((elapsed - FLIGHT_START) / 800));
        frame = requestAnimationFrame(render);
    };
    scene.style.opacity = '0';
    frame = requestAnimationFrame(render);
    await done;
}
